import type { RpSectionType } from '@mlm/shared';
import { embedHostnames } from '../../lib/member-origin';

/**
 * What the inspector shows for each section type.
 *
 * Declarative rather than ten bespoke inspector components: the shapes are known
 * and mostly repeat (a heading, some copy, a list, a button), so a spec table
 * gives every section the same controls, the same labels and the same reset
 * behavior for free. Same instinct as `RepeatableRows`' `RowField` union and the
 * Site Pages manifest — and it keeps this a TYPED editor, never a JSON textarea.
 *
 * Adding a section type means adding its spec here; a type with no spec falls
 * back to a "no fields" note rather than breaking the page.
 */

export type RpFieldSpec =
  | { kind: 'text'; key: string; label: string; placeholder?: string; hint?: string }
  | { kind: 'textarea'; key: string; label: string; placeholder?: string; hint?: string }
  | { kind: 'richtext'; key: string; label: string; hint?: string }
  | { kind: 'number'; key: string; label: string; min?: number; max?: number; hint?: string }
  | { kind: 'toggle'; key: string; label: string; hint?: string }
  | {
      kind: 'segmented';
      key: string;
      label: string;
      options: readonly { value: string | number; label: string }[];
      hint?: string;
    }
  /** A single uploaded image, stored as an R2 key. */
  | { kind: 'imageKey'; key: string; label: string; hint?: string }
  /** An `RpMediaRef` — image upload or a video URL. */
  | { kind: 'media'; key: string; label: string; hint?: string }
  /** An `RpButton` (label + typed link target). */
  | { kind: 'button'; key: string; label: string; optional?: boolean; hint?: string }
  /** A `string[]` list. */
  | {
      kind: 'strings';
      key: string;
      label: string;
      max: number;
      placeholder?: string;
      hint?: string;
    }
  /** An object list, edited as rows of sub-fields. */
  | {
      kind: 'items';
      key: string;
      label: string;
      max: number;
      itemFields: readonly RpItemFieldSpec[];
      hint?: string;
    };

export type RpItemFieldSpec =
  | { kind: 'text'; key: string; label: string; placeholder?: string }
  | { kind: 'textarea'; key: string; label: string; placeholder?: string }
  | { kind: 'icon'; key: string; label: string }
  | { kind: 'imageKey'; key: string; label: string };

/**
 * One line under every video URL box. Vimeo's "Only on domains I choose" privacy
 * setting 403s the player with "Because of its privacy settings, this video
 * cannot be played here" — indistinguishable from a bad URL from the admin's
 * side, and unfixable from this app. Naming the exact hostnames is the point:
 * they are what has to be pasted into Vimeo.
 */
const EMBED_HOSTS = embedHostnames();
export const RP_VIDEO_EMBED_HINT =
  EMBED_HOSTS.length > 0
    ? `Vimeo videos set to “Only on domains I choose” will not play until this site is allowed. On Vimeo: video → Settings → Privacy → “Where can this be embedded?” → add ${EMBED_HOSTS.join(', ')}.`
    : 'Vimeo videos set to “Only on domains I choose” will not play until this site’s domain is added under the video’s Privacy → embed settings on Vimeo.';

const HEADING_FIELDS: readonly RpFieldSpec[] = [
  { kind: 'text', key: 'eyebrow', label: 'Eyebrow', placeholder: 'Small label above the heading' },
  { kind: 'text', key: 'heading', label: 'Heading' },
  { kind: 'textarea', key: 'subheading', label: 'Sub-heading' },
];

export const RP_FIELD_SPECS: Partial<Record<RpSectionType, readonly RpFieldSpec[]>> = {
  hero: [
    {
      kind: 'segmented',
      key: 'variant',
      label: 'Layout',
      options: [
        { value: 'imageBg', label: 'Background' },
        { value: 'split', label: 'Split' },
        { value: 'plain', label: 'Plain' },
      ],
      hint: 'Background puts the copy over the band image (set one in Style). Split places media beside it.',
    },
    ...HEADING_FIELDS,
    { kind: 'media', key: 'media', label: 'Media (Split layout only)' },
    { kind: 'strings', key: 'bullets', label: 'Bullets', max: 6, placeholder: 'One selling point' },
    { kind: 'button', key: 'primaryCta', label: 'Primary button', optional: true },
    { kind: 'button', key: 'secondaryCta', label: 'Secondary button', optional: true },
    {
      kind: 'segmented',
      key: 'minHeight',
      label: 'Height',
      options: [
        { value: 'sm', label: 'Short' },
        { value: 'md', label: 'Medium' },
        { value: 'lg', label: 'Tall' },
      ],
    },
  ],
  cta: [
    ...HEADING_FIELDS,
    { kind: 'button', key: 'primaryCta', label: 'Primary button' },
    { kind: 'button', key: 'secondaryCta', label: 'Secondary button', optional: true },
  ],
  mediaText: [
    {
      kind: 'segmented',
      key: 'variant',
      label: 'Media side',
      options: [
        { value: 'mediaLeft', label: 'Left' },
        { value: 'mediaRight', label: 'Right' },
      ],
    },
    { kind: 'media', key: 'media', label: 'Media' },
    { kind: 'text', key: 'eyebrow', label: 'Eyebrow' },
    { kind: 'text', key: 'heading', label: 'Heading' },
    {
      kind: 'textarea',
      key: 'body',
      label: 'Body',
      hint: 'Leave a blank line between paragraphs.',
    },
    { kind: 'strings', key: 'bullets', label: 'Bullets', max: 8, placeholder: 'One point' },
    { kind: 'button', key: 'cta', label: 'Button', optional: true },
  ],
  featureGrid: [
    { kind: 'text', key: 'heading', label: 'Heading' },
    { kind: 'textarea', key: 'subheading', label: 'Sub-heading' },
    {
      kind: 'segmented',
      key: 'cols',
      label: 'Columns',
      options: [
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
      ],
    },
    {
      kind: 'segmented',
      key: 'cardStyle',
      label: 'Card style',
      options: [
        { value: 'plain', label: 'Plain' },
        { value: 'card', label: 'Card' },
        { value: 'bordered', label: 'Bordered' },
      ],
    },
    {
      kind: 'items',
      key: 'items',
      label: 'Benefits',
      max: 12,
      itemFields: [
        { kind: 'text', key: 'title', label: 'Title' },
        { kind: 'textarea', key: 'body', label: 'Body' },
        { kind: 'icon', key: 'icon', label: 'Icon' },
        { kind: 'imageKey', key: 'imageKey', label: 'Image (instead of an icon)' },
      ],
    },
  ],
  packages: [
    { kind: 'text', key: 'heading', label: 'Heading' },
    { kind: 'textarea', key: 'subheading', label: 'Sub-heading' },
    {
      kind: 'segmented',
      key: 'cols',
      label: 'Columns',
      options: [
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
      ],
    },
    { kind: 'text', key: 'ctaLabel', label: 'Button text', placeholder: 'Get started' },
    { kind: 'toggle', key: 'showPrice', label: 'Show price' },
    { kind: 'toggle', key: 'showPv', label: 'Show PV' },
  ],
  countdown: [
    { kind: 'text', key: 'label', label: 'Label', placeholder: 'Offer ends in' },
    {
      kind: 'number',
      key: 'hours',
      label: 'Hours',
      min: 1,
      max: 720,
      hint: 'Each visitor starts at the full duration on their first visit, then it restarts.',
    },
    {
      kind: 'segmented',
      key: 'variant',
      label: 'Style',
      options: [
        { value: 'bar', label: 'Bar' },
        { value: 'blocks', label: 'Blocks' },
      ],
    },
  ],
  steps: [
    { kind: 'text', key: 'heading', label: 'Heading' },
    { kind: 'textarea', key: 'subheading', label: 'Sub-heading' },
    {
      kind: 'segmented',
      key: 'layout',
      label: 'Layout',
      options: [
        { value: 'row', label: 'Across' },
        { value: 'stack', label: 'Stacked' },
      ],
    },
    {
      kind: 'items',
      key: 'items',
      label: 'Steps',
      max: 8,
      hint: 'Numbers come from the order — reordering renumbers them.',
      itemFields: [
        { kind: 'text', key: 'title', label: 'Title' },
        { kind: 'textarea', key: 'body', label: 'Body' },
      ],
    },
  ],
  testimonials: [
    { kind: 'text', key: 'heading', label: 'Heading' },
    { kind: 'textarea', key: 'subheading', label: 'Sub-heading' },
    {
      kind: 'segmented',
      key: 'cols',
      label: 'Columns',
      options: [
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
      ],
    },
    {
      kind: 'items',
      key: 'items',
      label: 'Quotes',
      max: 9,
      itemFields: [
        { kind: 'textarea', key: 'quote', label: 'Quote' },
        { kind: 'text', key: 'name', label: 'Name' },
        { kind: 'text', key: 'role', label: 'Role / location' },
        { kind: 'imageKey', key: 'avatarKey', label: 'Photo' },
      ],
    },
  ],
  faq: [
    { kind: 'text', key: 'heading', label: 'Heading' },
    { kind: 'textarea', key: 'subheading', label: 'Sub-heading' },
    {
      kind: 'items',
      key: 'items',
      label: 'Questions',
      max: 20,
      itemFields: [
        { kind: 'text', key: 'q', label: 'Question' },
        { kind: 'textarea', key: 'a', label: 'Answer' },
      ],
    },
  ],
  stats: [
    { kind: 'text', key: 'heading', label: 'Heading' },
    {
      kind: 'items',
      key: 'items',
      label: 'Figures',
      max: 6,
      itemFields: [
        { kind: 'text', key: 'value', label: 'Number', placeholder: '12,000+' },
        { kind: 'text', key: 'label', label: 'Label', placeholder: 'Members' },
        { kind: 'icon', key: 'icon', label: 'Icon' },
      ],
    },
  ],
  logoStrip: [
    { kind: 'text', key: 'heading', label: 'Heading' },
    { kind: 'toggle', key: 'grayscale', label: 'Fade the logos to grey' },
    {
      kind: 'items',
      key: 'items',
      label: 'Logos',
      max: 12,
      itemFields: [
        { kind: 'imageKey', key: 'imageKey', label: 'Logo' },
        { kind: 'text', key: 'alt', label: 'Name' },
      ],
    },
  ],
  gallery: [
    { kind: 'text', key: 'heading', label: 'Heading' },
    {
      kind: 'segmented',
      key: 'cols',
      label: 'Columns',
      options: [
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
      ],
    },
    {
      kind: 'items',
      key: 'items',
      label: 'Photos',
      max: 16,
      itemFields: [
        { kind: 'imageKey', key: 'imageKey', label: 'Photo' },
        { kind: 'text', key: 'caption', label: 'Caption' },
      ],
    },
  ],
  heading: [
    { kind: 'text', key: 'text', label: 'Text' },
    {
      kind: 'segmented',
      key: 'level',
      label: 'Size',
      options: [
        { value: 1, label: 'H1' },
        { value: 2, label: 'H2' },
        { value: 3, label: 'H3' },
      ],
      hint: 'Use one H1 per page — it is what search engines read as the title.',
    },
    { kind: 'text', key: 'eyebrow', label: 'Eyebrow' },
    { kind: 'textarea', key: 'subheading', label: 'Sub-heading' },
  ],
  richText: [{ kind: 'richtext', key: 'html', label: 'Text' }],
  image: [
    { kind: 'media', key: 'media', label: 'Image' },
    { kind: 'text', key: 'caption', label: 'Caption' },
    {
      kind: 'segmented',
      key: 'maxWidth',
      label: 'Max width',
      options: [
        { value: 'sm', label: 'S' },
        { value: 'md', label: 'M' },
        { value: 'lg', label: 'L' },
        { value: 'full', label: 'Full' },
      ],
    },
    { kind: 'button', key: 'link', label: 'Links to', optional: true },
  ],
  video: [
    {
      kind: 'text',
      key: 'url',
      label: 'Video URL',
      placeholder: 'YouTube, Vimeo or an .mp4 link',
      hint: RP_VIDEO_EMBED_HINT,
    },
    { kind: 'text', key: 'caption', label: 'Caption' },
    {
      kind: 'segmented',
      key: 'aspect',
      label: 'Shape',
      options: [
        { value: '16:9', label: 'Wide' },
        { value: '4:3', label: 'Classic' },
        { value: '9:16', label: 'Vertical' },
      ],
    },
  ],
  buttonGroup: [
    {
      kind: 'segmented',
      key: 'layout',
      label: 'Direction',
      options: [
        { value: 'row', label: 'Side by side' },
        { value: 'stack', label: 'Stacked' },
      ],
    },
  ],
  spacer: [
    {
      kind: 'segmented',
      key: 'height',
      label: 'Height',
      options: [
        { value: 'sm', label: 'S' },
        { value: 'md', label: 'M' },
        { value: 'lg', label: 'L' },
        { value: 'xl', label: 'XL' },
      ],
    },
    { kind: 'toggle', key: 'divider', label: 'Show a divider line' },
  ],
};
